import { useCallback, useId, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { type CSSProperties } from 'styled-components';
import type { WatermarkTemplate } from '@frameio/graphql-client/generated';
import {
  FeatureGate,
  FeatureGateConditionalRender,
} from '@frameio/plan-management/feature-gate/components';
import { useFeatureGatePlanSuggestion } from '@frameio/plan-management/feature-gate/hooks';
import { IconLockFeatureGate } from '@frameio/shared/icons';
import { EMPTY_ARRAY } from '@frameio/utils';
import { formatNumber } from '@frameio/utils/formatters';
import {
  AlertBanner,
  AlertDialog,
  AlertDialogButton,
  Box,
  Button2,
  List,
  ListItem,
  ListSearch,
  Separator,
  Text,
  theme,
  useMeasure,
} from '@frameio/vapor';
import { IconPlus } from '@frameio/vapor-icons';
import { SIDEPANEL_WIDTH } from '../constants';
import { getFriendlyTemplateData } from '../utils/getFriendlyTemplateData';
import { WatermarkTemplateListItem } from './components/WatermarkTemplateListItem';
import WatermarkTemplatePreview from './components/WatermarkTemplatePreview';
import TEXT from './generated/TEXT/WatermarkTemplateSelector';

const listItemPadding = 12;
const spaceBetweenItems = 10;
const Z_INDEX = {
  infoBanner: 3,
  selectorActive: 1,
  selectorInactive: 0,
};
export const animationVariants = {
  infoBanner: {
    variants: {
      enter: {
        y: -25,
        opacity: 0,
      },
      center: {
        y: 0,
        opacity: 1,
      },
      exit: {
        y: -25,
        opacity: 0,
      },
    },
    transition: {
      ease: 'easeInOut',
      duration: 0.2,
    },
  },
  selectorComponent: {
    variants: {
      enter: {
        x: -100,
        opacity: 0,
      },
      center: {
        zIndex: Z_INDEX.selectorActive,
        x: 0,
        opacity: 1,
      },
      exit: {
        zIndex: Z_INDEX.selectorInactive,
        x: -100,
        opacity: 0,
      },
    },
    transition: {
      ease: 'easeInOut',
      duration: 0.2,
    },
  },
};
const feature = 'UNLIMITED_WATERMARK_TEMPLATES';

TEXT.DEFINE({
  /** Label for all templates filter */
  allTemplates: 'All Templates',
  /** Label for add template button */
  addTemplate: 'Add Template',
  /** Label for clearing the template search input text */
  clearSearch: 'Clear template search input',
  /** Dot separator character */
  dotSeparator: '•',
  /** Delete confirmation dialog text */
  deleteConfirmationDialog: {
    /** Dialog title */
    title: 'Delete template?',
    /** Dialog details explaining deletion */
    details:
      'Deletion is permanent, but this template will still be applied to assets in any active Shares currently using it.',
    /** Cancel button label */
    cancel: 'Cancel',
    /** Delete button label */
    delete: 'Delete',
  },
  /** Label for accessing previously created templates */
  accessPreviouslyCreatedTemplates: 'Access previously created templates',
  /** Notice about name and email */
  nameEmailNotice:
    'For signed out reviewers, Watermark will display the Share title and creator name in place of name and email.',
  /** Label for pending template search */
  searchPending: 'Searching',
  /** Placeholder for template search input */
  searchPlaceholder: 'Search template',
});

const TEXT_STYLE: CSSProperties = { whiteSpace: 'nowrap' };

const TemplateListSection = ({
  sectionTitle,
  count,
  id,
}: {
  sectionTitle: string;
  count: number;
  id: string;
}) => {
  return (
    <Box alignItems="center" display="flex" style={{ gap: theme.spaces[6] }}>
      <Text
        color="tertiary"
        fontWeight="medium"
        id={id}
        style={TEXT_STYLE}
        variant="body"
      >
        {sectionTitle}
      </Text>
      <Text color="tertiary" fontWeight="medium" variant="body">
        {TEXT.dotSeparator}
      </Text>
      <Text color="tertiary" fontWeight="medium" variant="body">
        {formatNumber(count)}
      </Text>
    </Box>
  );
};

type WatermarkTemplateSelectorProps = {
  hasSessionBasedTemplates?: boolean;
  selectedWatermark: WatermarkTemplate;
  watermarkSelectionSet: Set<string>;
  setWatermarkSelectionSet: (selectionSet: Set<string>) => void;
  totalTemplatesCount: number;
  canManageTemplates: boolean;
  handleCreateNewTemplate: () => void;
  isLoading: boolean;
  handleEditTemplate: (template: WatermarkTemplate) => void;
  templates: WatermarkTemplate[];
  deleteTemplate: (templateId: string) => void;
  isShowing: boolean;
  scrollToId: (id: string) => void;
  scrolledId: string;
};

export const WatermarkTemplateSelector = ({
  hasSessionBasedTemplates,
  selectedWatermark,
  watermarkSelectionSet,
  setWatermarkSelectionSet,
  totalTemplatesCount,
  canManageTemplates,
  handleCreateNewTemplate,
  isLoading,
  handleEditTemplate,
  templates,
  deleteTemplate,
  scrollToId,
  scrolledId,
}: WatermarkTemplateSelectorProps) => {
  const [deleteConfirmationProcess, setDeleteConfirmationProcess] = useState<{
    showConfirmation: boolean;
    template: WatermarkTemplate | null;
  }>({
    showConfirmation: false,
    template: null,
  });

  const [containerRef, containerDimensions] = useMeasure();
  const [listItemRef, listItemDimensions] = useMeasure();

  const scrollable = useMemo(() => {
    return (
      totalTemplatesCount *
        (listItemDimensions.height + 2 * listItemPadding + spaceBetweenItems) -
        spaceBetweenItems >
      containerDimensions?.height
    );
  }, [
    containerDimensions?.height,
    listItemDimensions.height,
    totalTemplatesCount,
  ]);

  const friendlyTemplateData = useMemo(
    () =>
      getFriendlyTemplateData([
        // Include the selected watermark in the list if it has been deleted.
        ...(selectedWatermark?.deletedAt ? [selectedWatermark] : []),
        ...(templates || []),
      ]),
    [templates, selectedWatermark]
  );

  const featureGatePlanSuggestion = useFeatureGatePlanSuggestion(
    'SESSION_BASED_WATERMARKING'
  );
  const sessionBasedWatermarkingIncluded =
    featureGatePlanSuggestion?.includedInCurrentPlan;

  const resetDeleteConfirmation = () => {
    setDeleteConfirmationProcess({
      showConfirmation: false,
      template: null,
    });
  };

  const showDeleteConfirmation = useCallback(
    (template: WatermarkTemplate) => {
      setDeleteConfirmationProcess({
        showConfirmation: true,
        template,
      });
    },
    [setDeleteConfirmationProcess]
  );

  const handleDeleteTemplate = useCallback(
    (item: WatermarkTemplate) => {
      const index = templates?.findIndex(
        (template) => template?.id === item?.id
      );
      const previousItem = templates?.[index - 1 >= 0 ? index - 1 : 0];

      // Set the selection to the previous item in the list.
      if (previousItem) {
        setWatermarkSelectionSet(new Set([previousItem.id]));
        scrollToId(previousItem.id);
      }

      // Delete the selected watermark.
      deleteTemplate(item.id);

      // Close and reset the delete confirmation dialog.
      resetDeleteConfirmation();
    },
    [deleteTemplate, templates, setWatermarkSelectionSet, scrollToId]
  );

  const sectionId = useId();

  const selectedWatermarkHasUserNameOrEmailDataPoints = useMemo(() => {
    return selectedWatermark?.blocks?.some(
      (block) =>
        block?.dataPoints?.some(
          (dataPoint) =>
            dataPoint?.__typename === 'WatermarkDataPointUserName' ||
            dataPoint?.__typename === 'WatermarkDataPointEmail'
        )
    );
  }, [selectedWatermark]);

  const watermarkHasUserNameOrEmailDataPoints = useCallback(
    (item) => {
      const template = templates?.find((t) => t?.id === item?.id);
      return template?.blocks?.some(
        (block) =>
          block?.dataPoints?.some(
            (dataPoint) =>
              dataPoint?.__typename === 'WatermarkDataPointUserName' ||
              dataPoint?.__typename === 'WatermarkDataPointEmail'
          )
      );
    },
    [templates]
  );

  const isTemplateReadOnly = useCallback(
    (item: WatermarkTemplate) => {
      return (
        item?.id?.includes('optimistic') ||
        Boolean(item?.deletedAt) ||
        (!sessionBasedWatermarkingIncluded &&
          watermarkHasUserNameOrEmailDataPoints(item))
      );
    },
    [sessionBasedWatermarkingIncluded, watermarkHasUserNameOrEmailDataPoints]
  );

  return (
    <StyledMotionContainer
      animate="center"
      exit="exit"
      initial="enter"
      transition={animationVariants.selectorComponent.transition}
      variants={animationVariants.selectorComponent.variants}
    >
      <Box
        data-testid="watermark-template-selector-content"
        display="flex"
        height="100%"
      >
        <Box
          flexDirection="column"
          flexGrow="1"
          position="relative"
          style={{
            backgroundColor: theme.colors.components.player.background.media,
          }}
        >
          <AnimatePresence initial={false}>
            {selectedWatermarkHasUserNameOrEmailDataPoints && (
              <StyledMotionInfoBannerContainer
                animate="center"
                exit="exit"
                initial="enter"
                transition={animationVariants.infoBanner.transition}
                variants={animationVariants.infoBanner.variants}
              >
                <AlertBanner
                  title={TEXT.nameEmailNotice}
                  variant="informative"
                />
              </StyledMotionInfoBannerContainer>
            )}
          </AnimatePresence>

          <WatermarkTemplatePreview
            watermarkTemplateEntity={selectedWatermark}
          />
        </Box>
        <Box display="flex" flexDirection="column" width={SIDEPANEL_WIDTH}>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            style={{
              gap: theme.spaces[8],
              padding: `${theme.spaces[12]} ${theme.spaces[24]}`,
              borderBottom: `1px solid ${theme.colors.border.primary}`,
            }}
          >
            <TemplateListSection
              count={totalTemplatesCount}
              id={sectionId}
              sectionTitle={TEXT.allTemplates}
            />
            {canManageTemplates && (
              <FeatureGate feature={feature} shouldShowTooltip={true}>
                <FeatureGateConditionalRender
                  fallback={
                    <Button2
                      key="add-template-button"
                      data-testid="add-template-button"
                      iconBefore={<IconLockFeatureGate isActive={true} />}
                      size="regular"
                    >
                      {TEXT.addTemplate}
                    </Button2>
                  }
                  feature={feature}
                >
                  <Button2
                    key="add-template-button"
                    data-testid="add-template-button"
                    iconBefore={<IconPlus />}
                    onPress={handleCreateNewTemplate}
                    size="regular"
                  >
                    {TEXT.addTemplate}
                  </Button2>
                </FeatureGateConditionalRender>
              </FeatureGate>
            )}
          </Box>
          <List
            ref={
              containerRef as unknown as React.RefObject<HTMLDivElement | null>
            }
            animate={true}
            aria-labelledby={sectionId}
            disallowEmptySelection={true}
            estimatedItemHeight={160}
            itemGap={spaceBetweenItems}
            items={friendlyTemplateData || EMPTY_ARRAY}
            loading={isLoading ? 'loading' : undefined}
            onSelectionChange={(keys) =>
              setWatermarkSelectionSet(keys as Set<string>)
            }
            paddingY={listItemPadding}
            scrollPadding={{ top: listItemPadding, bottom: listItemPadding }}
            scrollToKey={scrolledId}
            search={
              <ListSearch
                clearButtonAriaLabel={TEXT.clearSearch}
                paddingX={16}
                pendingStateLabel={TEXT.searchPending}
                placeholder={TEXT.searchPlaceholder}
                separator={<div />}
                variant="default"
              />
            }
            selectedKeys={watermarkSelectionSet as Set<string>}
            selectionBehavior="toggle"
            selectionMode="single"
          >
            {(item: {
              id: string;
              name: string;
              templateDescription: string;
              deletedAt: string | null;
              inUse: boolean;
            }) => (
              <ListItem key={item?.id} textValue={item.name}>
                <WatermarkTemplateListItem
                  ref={listItemRef}
                  canManage={canManageTemplates}
                  isDeleteDisabled={templates?.length === 1 || item?.inUse}
                  isReadOnly={isTemplateReadOnly(item)}
                  item={item}
                  onDelete={showDeleteConfirmation}
                  onEdit={handleEditTemplate}
                />
              </ListItem>
            )}
          </List>

          {hasSessionBasedTemplates && !sessionBasedWatermarkingIncluded && (
            <>
              {scrollable && (
                <Separator
                  style={{
                    marginLeft: theme.spaces[16],
                    marginRight: theme.spaces[16],
                  }}
                />
              )}
              <Box
                alignItems="center"
                display="flex"
                paddingBottom={24}
                paddingLeft={24}
                paddingTop={24}
              >
                <FeatureGate
                  feature="SESSION_BASED_WATERMARKING"
                  shouldShowTooltip={true}
                >
                  <Box
                    alignItems="center"
                    display="flex"
                    gap={16}
                    paddingLeft={8}
                  >
                    <IconLockFeatureGate customSize={18} isActive={true} />
                    <Text color="secondary" variant="caption">
                      {TEXT.accessPreviouslyCreatedTemplates}
                    </Text>
                  </Box>
                </FeatureGate>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <AlertDialog
        actions={
          <>
            <AlertDialogButton onClick={resetDeleteConfirmation}>
              {TEXT.deleteConfirmationDialog.cancel}
            </AlertDialogButton>
            <AlertDialogButton
              color="error"
              data-testid="delete-confirmation-dialog-delete-button"
              onClick={() =>
                handleDeleteTemplate(
                  deleteConfirmationProcess.template as WatermarkTemplate
                )
              }
            >
              {TEXT.deleteConfirmationDialog.delete}
            </AlertDialogButton>
          </>
        }
        details={TEXT.deleteConfirmationDialog.details}
        isShowing={deleteConfirmationProcess.showConfirmation}
        title={TEXT.deleteConfirmationDialog.title}
        variant="error"
      />
    </StyledMotionContainer>
  );
};

const StyledMotionContainer = styled(motion.div)`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const StyledMotionInfoBannerContainer = styled(motion(Box))`
  padding: ${theme.spaces[2]};
  position: absolute;
  width: 100%;
  z-index: ${Z_INDEX.infoBanner};
`;
